<template>
  <div class="tbs">
    <div class="tbs-head">
      <div>
        <h3>Brand</h3>
        <p class="tbs-sub">These are the only things that appear in what your audience sees. Anything you don't
          set here uses the Yorlin default.</p>
      </div>
    </div>

    <div v-if="error" class="tbs-alert">{{ error }}</div>
    <div v-if="loading" class="tbs-loading">Loading your brand…</div>

    <div v-for="group in groups" :key="group.key" class="group">
      <div class="group-head">
        <span class="gi" :class="group.key"><CygnusIcon :name="group.icon" /></span>
        <div>
          <h4>{{ group.label }}</h4>
          <div class="gsub">{{ group.sub }}</div>
        </div>
      </div>

      <div class="group-body">
        <div class="fields">
          <div v-for="f in group.fields" :key="f.slug" class="field">
            <!-- indicator (swatch / logo / font tile) -->
            <span v-if="f.control === 'colour'" class="swatch" :style="{ background: effectiveColour(f) }"></span>
            <span v-else-if="f.control === 'logo'" class="logo-tile">
              <img v-if="effectiveLogo(f)" :src="effectiveLogo(f)" alt="" />
              <CygnusIcon name="file" v-else />
            </span>
            <span v-else class="logo-tile font-tile" :style="{ fontFamily: fontStack(f) }">Aa</span>

            <div class="fmeta">
              <div class="flabel">
                {{ f.label }}
                <span v-if="f.inherited" class="chip def"><span class="chip-dot"></span> Platform default</span>
                <span v-else class="chip custom">Customised</span>
              </div>
              <div class="fval">{{ fieldHint(f) }}</div>

              <!-- inline editors -->
              <div v-if="f.control === 'html' && f.editing" class="inline-edit">
                <textarea v-model="f.draft" rows="3" placeholder="HTML — sanitised on render"></textarea>
                <div class="inline-actions">
                  <button class="btn sm primary" @click="saveHtml(f)">Save</button>
                  <button class="btn sm ghost" @click="f.editing = false">Cancel</button>
                </div>
              </div>
              <div v-if="f.control === 'logo' && f.editing" class="inline-edit">
                <input v-model="f.draft" type="url" placeholder="https://…/your-logo.png" />
                <div class="inline-actions">
                  <button class="btn sm primary" @click="saveLogo(f)">Save</button>
                  <button class="btn sm ghost" @click="f.editing = false">Cancel</button>
                </div>
              </div>
            </div>

            <div class="factions">
              <!-- colour control -->
              <template v-if="f.control === 'colour'">
                <input type="color" class="cpick" :value="colourHex(f)" @change="saveColour(f, $event.target.value)" />
              </template>
              <!-- font control -->
              <template v-else-if="f.control === 'font'">
                <select class="fontpick" :value="fontFamily(f)" @change="saveFont(f, $event.target.value)">
                  <option v-for="fc in fontChoices" :key="fc.family" :value="fc.family">{{ fc.family }}</option>
                </select>
              </template>
              <!-- logo / html control -->
              <template v-else>
                <button class="btn sm ghost" @click="openEditor(f)">
                  {{ f.cascade_from && f.inherited ? 'Set separately' : 'Replace' }}
                </button>
              </template>

              <button v-if="!f.inherited" class="reset" title="Reset to Yorlin default" @click="resetField(f)">
                <CygnusIcon name="refresh" /> Reset
              </button>
            </div>
          </div>
        </div>

        <!-- live mini preview rail -->
        <div class="preview-rail">
          <div class="prl"><span class="lp"></span> Live preview</div>

          <div v-if="group.key === 'email'" class="mini-email">
            <div class="meh" :style="{ background: brandColour }">
              <span class="melogo">
                <img v-if="logoOf('logo')" :src="logoOf('logo')" alt="" />
                <span v-else>◆</span>
              </span>
              <span class="meh-name" :style="{ fontFamily: headingStack }">Your workspace</span>
            </div>
            <div class="mebody">
              <h5 :style="{ fontFamily: headingStack, color: bodyTextColour }">Your June update is ready</h5>
              <div class="metext" :style="{ fontFamily: bodyStack }">A short, warm note to your audience — new
                availability opened this week for members.</div>
              <span class="mebtn" :style="{ background: brandColour }">Reserve a place</span>
            </div>
          </div>

          <div v-else class="mini-hero">
            <div class="mhtop" :style="{ background: heroGradient }">
              <span class="mhlogo">
                <img v-if="landingLogo" :src="landingLogo" alt="" />
                <span v-else>◆</span>
              </span>
              <h5 :style="{ fontFamily: headingStack }">A page worth landing on</h5>
              <div class="mhsub" :style="{ fontFamily: bodyStack }">Members-only early access this week</div>
              <span class="mhbtn" :style="{ color: accentColour }">Reserve a place</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import useApi from '@apexl/icarus-core/src/services/api';

/**
 * TenantBrandSurface (Musubi) — the outcome-grouped brand editor a WORKSPACE
 * OWNER sees at /admin/branding (Ops::SETTINGS). Renders only the curated
 * tenant-visible slugs (BrandSurfaceCatalog), grouped by where they show up —
 * "Used in your emails" / "Used on your landing pages" — with a live mini
 * preview rail that repaints client-side as fields change. Operators never see
 * this; the BrandAdmin wrapper mounts the full vendor editor for them.
 *
 * Reads GET /api/v1/branding/surface; writes reuse the (regated) vendor asset
 * endpoints PUT/DELETE /api/v1/branding/assets/{slug}. No native dialogs —
 * logo/HTML edits use inline editors; reset reverts to inherited (low risk).
 */
export default {
  name: 'TenantBrandSurface',
  props: { apiBase: { type: String, default: '/api/v1' } },
  data() {
    return { api: useApi(), groups: [], fontChoices: [], loading: false, error: '' };
  },
  computed: {
    brandColour() { return this.colourOf('accent-stone', '#4F46E5'); },
    accentColour() { return this.colourOf('accent-light', '#EDE3D3'); },
    bodyTextColour() { return this.colourOf('text-primary', '#1A1B1E'); },
    headingStack() { return this.fontStackOf('font-display'); },
    bodyStack() { return this.fontStackOf('font-body'); },
    heroGradient() {
      const hero = this.field('accent-dark');
      const base = this.brandColour;
      // Inherited hero background derives from the brand colour (darker tone);
      // an override uses the hero-background value directly.
      const dark = hero && !hero.inherited ? (hero.value?.hex || base) : this.shade(base, -0.28);
      return `linear-gradient(135deg, ${base}, ${dark})`;
    },
    landingLogo() {
      const wm = this.logoOf('wordmark');
      return wm || this.logoOf('logo'); // landing logo cascades from the email logo
    },
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await this.api.get(`${this.apiBase}/branding/surface`);
        this.fontChoices = res?.font_choices || [];
        this.groups = (res?.groups || []).map(g => ({
          ...g,
          fields: (g.fields || []).map(f => ({ ...f, editing: false, draft: '' })),
        }));
      } catch (e) { this.error = 'Failed to load your brand.'; } finally { this.loading = false; }
    },

    // ---- field lookups (source of truth = groups[].fields[]) ---------------
    field(slug) {
      for (const g of this.groups) {
        const f = g.fields.find(x => x.slug === slug);
        if (f) return f;
      }
      return null;
    },
    colourOf(slug, fallback) { const f = this.field(slug); return (f && f.value?.hex) || fallback; },
    colourHex(f) { return f.value?.hex || '#000000'; },
    effectiveColour(f) {
      // A cascade colour that's still inherited shows the source it derives
      // from (hero background → brand colour), matching the live preview.
      if (f.cascade_from && f.inherited) return this.colourOf(f.cascade_from, f.value?.hex || '#e4e7ec');
      return f.value?.hex || '#e4e7ec';
    },
    fontFamily(f) { return f.value?.family || 'Inter'; },
    fontStack(f) { return this.stack(f.value); },
    fontStackOf(slug) { const f = this.field(slug); return this.stack(f && f.value); },
    stack(v) {
      if (!v || !v.family) return 'Inter, system-ui, sans-serif';
      const fb = Array.isArray(v.fallbacks) ? v.fallbacks.join(', ') : 'sans-serif';
      return `'${v.family}', ${fb}`;
    },
    logoOf(slug) { const f = this.field(slug); return (f && f.value?.url) || ''; },
    effectiveLogo(f) {
      if (f.value?.url) return f.value.url;
      if (f.cascade_from) return this.logoOf(f.cascade_from);
      return '';
    },
    fieldHint(f) {
      if (f.control === 'colour') return (f.value?.hex || '') + (f.cascade_from && f.inherited ? ' · derived from brand colour' : '');
      if (f.control === 'font') return f.value?.family || '';
      if (f.control === 'logo') return f.cascade_from && f.inherited ? f.hint : (f.value?.url ? 'Custom image set' : f.hint);
      if (f.control === 'html') return f.value?.html ? 'Custom HTML set' : f.hint;
      return f.hint;
    },

    // ---- saves (reuse regated vendor asset endpoints) ----------------------
    async putAsset(f, value) {
      await this.api.put(`${this.apiBase}/branding/assets/${f.slug}`, { asset_type: f.asset_type, value });
      f.value = value;
      f.inherited = false;
    },
    async saveColour(f, hex) {
      try { await this.putAsset(f, { hex }); } catch (e) { this.error = `Failed to save ${f.label}.`; }
    },
    async saveFont(f, family) {
      const choice = this.fontChoices.find(c => c.family === family) || { family, fallbacks: ['sans-serif'] };
      const value = { family: choice.family, weight: f.value?.weight || 400, fallbacks: choice.fallbacks };
      try { await this.putAsset(f, value); } catch (e) { this.error = `Failed to save ${f.label}.`; }
    },
    openEditor(f) {
      f.draft = f.control === 'html' ? (f.value?.html || '') : (f.value?.url || '');
      f.editing = true;
    },
    async saveLogo(f) {
      const url = (f.draft || '').trim();
      if (!url) { f.editing = false; return; }
      try { await this.putAsset(f, { url, alt: '' }); f.editing = false; } catch (e) { this.error = `Failed to save ${f.label}.`; }
    },
    async saveHtml(f) {
      try { await this.putAsset(f, { html: f.draft || '' }); f.editing = false; } catch (e) { this.error = `Failed to save ${f.label}.`; }
    },
    async resetField(f) {
      try {
        const res = await this.api.delete(`${this.apiBase}/branding/assets/${f.slug}`);
        f.value = res?.value || f.value;
        f.inherited = true;
        f.editing = false;
      } catch (e) { this.error = `Failed to reset ${f.label}.`; }
    },

    // Darken/lighten a hex colour for the derived hero gradient stop.
    shade(hex, amt) {
      const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ''));
      if (!m) return hex;
      const n = parseInt(m[1], 16);
      const clamp = v => Math.max(0, Math.min(255, Math.round(v)));
      const r = clamp((n >> 16) + 255 * amt);
      const g = clamp(((n >> 8) & 0xff) + 255 * amt);
      const b = clamp((n & 0xff) + 255 * amt);
      return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
    },
  },
};
</script>

<style scoped lang="scss">
.tbs {
  --line: var(--m-border, #e4e7ec);
  --line-2: var(--m-border, #eef0f3);
  --surface: var(--m-bg-card, #fff);
  --surface-2: #fbfbfd;
  --ink: var(--m-text-primary, #16181d);
  --ink-2: var(--m-text-secondary, #4a4f5c);
  --ink-3: var(--m-text-tertiary, #787f8c);
  --indigo: var(--m-accent, #4f46e5);
  --indigo-wash: var(--m-accent-soft, #eef0fe);
  --indigo-ink: var(--m-accent-hover, #3730a3);
  --teal: var(--m-accent-2, #14b8a6);
  color: var(--ink);

  .tbs-head { margin-bottom: 18px; }
  .tbs-head h3 { font-size: 19px; font-weight: 650; margin: 0 0 4px; }
  .tbs-sub { color: var(--ink-2); font-size: 13.5px; margin: 0; max-width: 640px; }
  .tbs-alert { background: #fbeaea; color: #b23636; border-radius: 8px; padding: 10px 14px; font-size: 13px; margin-bottom: 14px; }
  .tbs-loading { color: var(--ink-3); font-size: 13px; padding: 12px 0; }

  .group { border: 1px solid var(--line); border-radius: 14px; overflow: hidden; margin-bottom: 20px; background: var(--surface); }
  .group-head { display: flex; align-items: center; gap: 11px; padding: 14px 18px; background: var(--surface-2); border-bottom: 1px solid var(--line); }
  .group-head .gi { width: 30px; height: 30px; border-radius: 8px; display: grid; place-items: center; font-size: 14px; flex: none; }
  .gi.email { background: var(--indigo-wash); color: var(--indigo); }
  .gi.landing { background: #e9f7f1; color: #0e9e6e; }
  .group-head h4 { font-size: 14.5px; font-weight: 650; margin: 0; }
  .group-head .gsub { font-size: 12px; color: var(--ink-3); }

  .group-body { display: grid; grid-template-columns: 1fr 300px; }
  .fields { padding: 8px 18px 16px; }
  .field { display: flex; align-items: flex-start; gap: 14px; padding: 13px 0; border-bottom: 1px solid var(--line-2); }
  .field:last-child { border-bottom: none; }

  .swatch { width: 34px; height: 34px; border-radius: 9px; border: 1px solid rgba(0,0,0,.08); flex: none; margin-top: 2px; }
  .logo-tile { width: 34px; height: 34px; border-radius: 9px; background: var(--surface-2); border: 1px dashed var(--line); display: grid; place-items: center; font-size: 12px; color: var(--ink-3); flex: none; overflow: hidden; margin-top: 2px; }
  .logo-tile img { width: 100%; height: 100%; object-fit: contain; }
  .font-tile { border-style: solid; font-weight: 700; color: var(--ink); font-size: 15px; }

  .fmeta { flex: 1; min-width: 0; }
  .flabel { font-size: 13.5px; font-weight: 600; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .fval { font-size: 12px; color: var(--ink-3); margin-top: 2px; word-break: break-word; }

  .chip { display: inline-flex; align-items: center; gap: 5px; font-size: 10.5px; font-weight: 650; padding: 3px 9px; border-radius: 20px; white-space: nowrap; }
  .chip.def { background: #f1f3f6; color: #667085; }
  .chip.custom { background: var(--indigo-wash); color: var(--indigo-ink); }
  .chip-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }

  .factions { display: flex; align-items: center; gap: 8px; flex: none; }
  .cpick { width: 34px; height: 30px; padding: 2px; border: 1px solid var(--line); border-radius: 8px; background: #fff; cursor: pointer; }
  .fontpick { font-size: 13px; border: 1px solid var(--line); border-radius: 8px; padding: 6px 10px; background: #fff; color: var(--ink); min-width: 150px; }

  .btn { display: inline-flex; align-items: center; gap: 6px; border: 1px solid var(--line); background: #fff; color: var(--ink); font: inherit; font-size: 12px; font-weight: 600; padding: 6px 12px; border-radius: 8px; cursor: pointer; }
  .btn.primary { background: var(--indigo); border-color: var(--indigo); color: #fff; }
  .btn.ghost { background: transparent; }
  .btn.sm { padding: 5px 10px; font-size: 12px; }
  .reset { font-size: 11.5px; font-weight: 600; color: var(--ink-3); cursor: pointer; background: none; border: none; padding: 2px 4px; display: inline-flex; align-items: center; gap: 4px; }
  .reset:hover { color: var(--indigo); }

  .inline-edit { margin-top: 8px; }
  .inline-edit textarea, .inline-edit input { width: 100%; border: 1px solid var(--line); border-radius: 8px; padding: 8px 10px; font: inherit; font-size: 12.5px; color: var(--ink); background: var(--surface-2); }
  .inline-actions { display: flex; gap: 8px; margin-top: 6px; }

  /* preview rail */
  .preview-rail { border-left: 1px solid var(--line); background: var(--surface-2); padding: 16px; }
  .prl { font-size: 10.5px; text-transform: uppercase; letter-spacing: .08em; font-weight: 700; color: var(--ink-3); margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
  .prl .lp { width: 7px; height: 7px; border-radius: 50%; background: var(--teal); display: inline-block; animation: tbs-pulse 1.4s infinite; }
  @keyframes tbs-pulse { 0%,100% { opacity: .3; } 50% { opacity: 1; } }

  .mini-email, .mini-hero { border: 1px solid var(--line); border-radius: 10px; overflow: hidden; background: #fff; box-shadow: var(--m-shadow-card, 0 1px 2px rgba(16,24,40,.06)); }
  .mini-email .meh { padding: 14px 16px; display: flex; align-items: center; gap: 9px; }
  .melogo { width: 24px; height: 24px; border-radius: 6px; background: rgba(255,255,255,.2); display: grid; place-items: center; color: #fff; font-weight: 700; font-size: 11px; overflow: hidden; }
  .melogo img, .mhlogo img { width: 100%; height: 100%; object-fit: contain; }
  .meh-name { color: #fff; font-weight: 700; font-size: 12px; }
  .mebody { padding: 14px 16px 18px; }
  .mebody h5 { font-size: 14px; margin: 0 0 6px; }
  .metext { font-size: 11.5px; line-height: 1.5; color: #4a4f5c; }
  .mebtn { display: inline-block; margin-top: 12px; color: #fff; font-size: 11px; font-weight: 600; padding: 7px 14px; border-radius: 7px; }

  .mini-hero .mhtop { padding: 20px 16px 22px; text-align: center; color: #fff; }
  .mhlogo { width: 30px; height: 30px; margin: 0 auto 10px; border-radius: 8px; background: rgba(255,255,255,.2); display: grid; place-items: center; font-weight: 700; font-size: 12px; color: #fff; overflow: hidden; }
  .mini-hero h5 { font-size: 15px; margin: 0 0 6px; }
  .mhsub { font-size: 11px; opacity: .9; }
  .mhbtn { display: inline-block; margin-top: 12px; background: #fff; font-size: 11px; font-weight: 700; padding: 7px 15px; border-radius: 20px; }

  @media (max-width: 880px) {
    .group-body { grid-template-columns: 1fr; }
    .preview-rail { border-left: none; border-top: 1px solid var(--line); }
  }
}
</style>
